import type { BookingCouponsBulkPreviewRecord, Currency } from '@navio/nsdk';
import { z } from 'zod';
import { DISCOUNT_KIND } from '../../discountKind';
import { expiryFromDay } from '../../expiryFromDay';
import { refinePromotionDealFields } from '../../promotionDealValidation';
import { buildSuggestedPromotionCopy, type PromotionCopyLocale } from '../../suggestedPromotionCopy';

// Coupons given for bookings cover every product and are used once.
const ALL_PRODUCTS = {
  appliesToAllProducts: true,
  filterByProducts: false,
  filterByTags: false,
  salesItemIds: [],
  salesItemTagIds: []
};

export const bookingCouponSchema = z
  .object({
    discountKind: z.enum([DISCOUNT_KIND.amount, DISCOUNT_KIND.percentage]),
    discountAmount: z.number().nullable(),
    discountPercentage: z.number().nullable(),
    name: z.string(),
    description: z.string(),
    expiresAt: z.date().nullish()
  })
  .superRefine((value, ctx) => refinePromotionDealFields({ ...value, ...ALL_PRODUCTS }, ctx));

export type BookingCouponFormValues = z.input<typeof bookingCouponSchema>;

export const bookingCouponDefaultValues: BookingCouponFormValues = {
  discountKind: DISCOUNT_KIND.amount,
  discountAmount: null,
  discountPercentage: null,
  name: '',
  description: '',
  expiresAt: null
};

/** Name and description the customer sees: what was typed, else the suggested copy. */
export const bookingCouponCopy = ({
  values,
  currency,
  locale
}: {
  values: BookingCouponFormValues;
  currency: Currency | null;
  locale: PromotionCopyLocale;
}) => {
  const suggested = buildSuggestedPromotionCopy({ ...values, appliesToAllProducts: true, currency, locale });
  return {
    name: values.name.trim() || suggested.name,
    description: values.description.trim() || suggested.description
  };
};

export const bookingCouponPayload = ({
  values,
  currency,
  locale
}: {
  values: BookingCouponFormValues;
  currency: Currency;
  locale: PromotionCopyLocale;
}): Omit<BookingCouponsBulkPreviewRecord, 'bookingIds' | 'target'> => {
  const copy = bookingCouponCopy({ values, currency, locale });
  return {
    name: copy.name,
    descriptionPublic: copy.description,
    discount:
      values.discountKind === DISCOUNT_KIND.amount && values.discountAmount !== null
        ? { amount: values.discountAmount, currency }
        : null,
    discountPercentage: values.discountKind === DISCOUNT_KIND.percentage ? values.discountPercentage : null,
    expiresAt: values.expiresAt ? expiryFromDay(values.expiresAt) : null
  };
};
